import type { Moment } from 'moment-timezone';
import type Marketplace from '@spike/marketplace-model';
import { BusinessHours } from '@spike/model';
import type { Enrollment, MembershipItem } from '@spike/membership-model';
import { MEMBERSHIP_ITEM_TYPES, MembershipItemType } from '@spike/membership-model';
import type { MembershipsSelection } from '../UI/MembershipsSelector/MembershipsSelector';

export function getTodayClosingTime(from: Moment, marketplace: Marketplace): Moment {
    const dayOfWeek = from.format('dddd').toLowerCase();
    const businessHours: BusinessHours | null =
        marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
    const [hour, minute] = businessHours?.close.split(':').map(Number) || [17, 0];
    return from.clone().hour(hour).minute(minute);
}

export function getTodayOpeningTime(from: Moment, marketplace: Marketplace): Moment {
    const dayOfWeek = from.format('dddd').toLowerCase();
    const businessHours: BusinessHours | null =
        marketplace.schedule.week[dayOfWeek as keyof typeof marketplace.schedule.week];
    const [hour, minute] = businessHours?.open?.split(':').map(Number) || [8, 0];
    return from
        .clone()
        .tz(marketplace.schedule.timeZone)
        .startOf('day')
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
}

export type MembershipDrivenServiceFlow =
    | 'grooming'
    | 'dental'
    | 'daycare'
    | 'boarding'
    | 'transportation'
    | 'walking'
    | 'training';

export function flowFromMembershipItemType(itemType: string): MembershipDrivenServiceFlow | undefined {
    switch (itemType) {
        case MEMBERSHIP_ITEM_TYPES.GROOMING_SERVICE:
            return 'grooming';
        case MEMBERSHIP_ITEM_TYPES.DAYCARE_SERVICE:
            return 'daycare';
        case MEMBERSHIP_ITEM_TYPES.BOARDING_SERVICE:
            return 'boarding';
        case MEMBERSHIP_ITEM_TYPES.TRANSPORTATION_SERVICE:
            return 'transportation';
        case MEMBERSHIP_ITEM_TYPES.WALKING_SERVICE:
            return 'walking';
        case MEMBERSHIP_ITEM_TYPES.TRAINING_SERVICE:
            return 'training';
        default:
            return undefined;
    }
}

export function parseMembershipServiceId(item: MembershipItem): number | undefined {
    const n = parseInt(String(item.itemID ?? '').trim(), 10);
    return Number.isFinite(n) ? n : undefined;
}

export function flowFromPetServiceBusinessArea(businessArea: string | undefined): MembershipDrivenServiceFlow {
    if (businessArea === 'dental') return 'dental';
    if (businessArea === 'daycare') return 'daycare';
    if (businessArea === 'boarding') return 'boarding';
    if (businessArea === 'transportation') return 'transportation';
    if (businessArea === 'walking') return 'walking';
    if (businessArea === 'training') return 'training';
    return 'grooming';
}

export function membershipItemNeedsPetServicesLookup(itemType: string): boolean {
    return itemType === MembershipItemType.SERVICE || itemType === 'Primary Service' || itemType === 'Service';
}

export function membershipIdFromSelection(selection: MembershipsSelection): number | undefined {
    const id = selection.membership.id ?? selection.item.membershipID;
    return id != null && Number.isFinite(Number(id)) ? Number(id) : undefined;
}

export function enrollmentIdForPetMembership(
    enrollments: Enrollment[],
    membershipId: number,
    petId: number
): number | undefined {
    const match = enrollments.find(
        e => e.membership?.id === membershipId && e.pets?.some(p => p.id === petId) && e.status !== 'canceled'
    );
    return match?.id;
}
